import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Trash2 } from "lucide-react";
import QuickReminderDialog from "@/components/QuickReminderDialog";
import MioGiorno from "@/components/MioGiorno";
import AppuntamentoDialog from "@/components/AppuntamentoDialog";
import SceltaClienteDialog from "@/components/SceltaClienteDialog";
import DocumentoDialog from "@/components/GeneraDocumentoDialog";
import { ContactsMap } from "@/components/ContactsMap";
import ScadenzeDocumenti from "@/components/ScadenzeDocumenti";
import NuoviDocumenti from "@/components/NuoviDocumenti";
import SalutoOrari from "@/components/SalutoOrari";
import PrimoPiano from "@/components/PrimoPiano";
import Sezione from "@/components/Sezione";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/format";

const dataIt = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "");

function Cifra({ etichetta, valore, spiega, tono, testid, fondo, to, href }) {
  const corpo = (
    <>
      <span className="eti">{etichetta}</span>
      <b className={tono}>{valore}</b>
      <small>{spiega}</small>
    </>
  );
  const cls = "cifra" + (to || href ? " hover:bg-[#FAFAF7] transition-colors no-underline" : "");
  const stile = fondo ? { background: "#F7F6F2" } : undefined;
  if (to) return <Link data-testid={testid} to={to} className={cls} style={stile}>{corpo}</Link>;
  if (href) return <a data-testid={testid} href={href} target="_blank" rel="noreferrer" className={cls} style={stile}>{corpo}</a>;
  return <div data-testid={testid} className="cifra" style={stile}>{corpo}</div>;
}

export default function Dashboard() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const vedeSoldi = can("vedi_dati_economici");
  const { data } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const { data: gmail } = useQuery({ queryKey: ["gmail-recent"], queryFn: async () => (await api.get("/gmail/recent")).data, refetchInterval: 120000 });
  const { data: news } = useQuery({ queryKey: ["news"], queryFn: async () => (await api.get("/news")).data, refetchInterval: 1800000, staleTime: 1800000 });
  const { data: pratiche } = useQuery({ queryKey: ["pratiche"], queryFn: async () => (await api.get("/pratiche")).data });
  const { data: attenzione } = useQuery({ queryKey: ["attenzione"], queryFn: async () => (await api.get("/attenzione")).data, refetchInterval: 60000 });
  const [q, setQ] = useState("");
  const [tipoF, setTipoF] = useState("");
  const [reminderOpen, setReminderOpen] = useState(false);
  const [appOpen, setAppOpen] = useState(false);
  const [sceltaOpen, setSceltaOpen] = useState(false);
  const [docCliente, setDocCliente] = useState(null);
  const [calDays, setCalDays] = useState(7);
  const [calStudio, setCalStudio] = useState(true);

  const allPratiche = pratiche || [];
  const tipiList = [...new Set(allPratiche.map((p) => p.tipo_pratica_nome))].sort();
  const searchActive = q.trim().length > 0 || tipoF;
  const foundPratiche = !searchActive ? [] : allPratiche.filter((p) => {
    const name = `${p.cliente?.nome || ""} ${p.cliente?.cognome || ""}`.toLowerCase();
    return (!q.trim() || name.includes(q.trim().toLowerCase())) && (!tipoF || p.tipo_pratica_nome === tipoF);
  });
  const d = data || {};

  const { data: cal, isLoading: calLoading } = useQuery({
    queryKey: ["calendar-events", calDays, calStudio],
    queryFn: async () => (await api.get(`/calendar/events?days=${calDays}&altre=${calStudio ? 1 : 0}`)).data,
  });
  const events = cal?.events || [];
  const eliminaEvento = async (e) => {
    if (!window.confirm(`Eliminare "${e.summary}" dal calendario?`)) return;
    try {
      await api.delete(`/calendar/events/${e.id}?calendario_user_id=${e.calendario_user_id || ""}`);
      toast.success("Evento eliminato dal calendario");
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
    } catch (err) { toast.error(err?.response?.data?.detail || "Eliminazione non riuscita"); }
  };
  const eventsByDay = events.reduce((acc, e) => {
    const key = new Date(e.start).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
    (acc[key] = acc[key] || []).push(e);
    return acc;
  }, {});

  return (
    <div data-testid="dashboard-page"
      className="scheda-rb -m-4 sm:-m-5 lg:-m-7 p-4 sm:p-5 lg:p-7 bg-[#F2F1EC] min-h-screen">
      <div className="w-full max-w-[1120px] mx-auto flex flex-col gap-[18px]">

        <SalutoOrari>
          <Link data-testid="tile-clienti" to="/clienti" className="btn btn-g btn-larghezza sm:w-auto">
            Clienti{attenzione?.n_clienti > 0 ? ` · ${attenzione.n_clienti} da completare` : ""}
          </Link>
          <Link data-testid="tile-pratiche" to="/pratiche" className="btn btn-o btn-larghezza sm:w-auto">
            Pratiche{attenzione?.n_pratiche > 0 ? ` · ${attenzione.n_pratiche} da completare` : ""}
          </Link>
          <button data-testid="tile-genera-documento" onClick={() => setSceltaOpen(true)} className="btn btn-o btn-larghezza sm:w-auto">
            Genera documento
          </button>
          <button data-testid="tile-appuntamento" onClick={() => setAppOpen(true)} className="btn btn-o btn-larghezza sm:w-auto">
            Crea appuntamento
          </button>
        </SalutoOrari>

        {/* 1 · Lo studio in numeri */}
        <Sezione testid="sez-numeri" titolo="Lo studio oggi"
          azioni={<span className="text-[13px] text-[#E7D9B2]">aggiornato in tempo reale</span>}>
          <div className="cifre" data-testid="home-tiles">
            <Cifra testid="tile-info-clienti" etichetta="Clienti gestiti" valore={d.clienti ?? 0} to="/clienti"
              tono="text-[#1B2A4B]" spiega="in anagrafica" />
            <Cifra testid="tile-info-pratiche" etichetta="Pratiche aperte" valore={d.pratiche_aperte ?? 0} to="/pratiche"
              tono="text-[#1B2A4B]" spiega="ancora in corso" />
            {vedeSoldi && (
              <Cifra testid="tile-info-incassare" etichetta="Onorari da incassare" valore={eur(d.da_incassare)} fondo
                tono="text-[#1A1A1A]" spiega="somma di tutte le pratiche" />
            )}
            <Cifra testid="tile-info-email" etichetta="Email da leggere" valore={gmail?.unread_count ?? "—"}
              href={gmail?.gmail_link || "https://mail.google.com"} tono="text-[#1B2A4B]" spiega="nella casella dello studio" />
          </div>
          {(d.pratiche_per_origine || []).length > 0 && (
            <div data-testid="origini-pratiche" className="flex flex-wrap gap-2">
              {d.pratiche_per_origine.map((o) => (
                <Link key={o.origine} to={`/pratiche?origine=${o.origine}`} data-testid={`origine-count-${o.origine}`}
                  className="text-[13px] text-[#4A4A48] bg-[#ECEAE3] rounded-[3px] px-3 py-1.5 no-underline hover:text-[#1B2A4B]">
                  {o.label} <b className="text-[#8A6E2F]">{o.totale}</b>
                </Link>
              ))}
            </div>
          )}
        </Sezione>

        {/* 2 · In primo piano */}
        <Sezione testid="sez-primo-piano" titolo="In primo piano">
          <PrimoPiano />
        </Sezione>

        {/* 3 · Agenda */}
        <Sezione testid="sez-agenda" titolo="Agenda" azioni={
          <>
            {cal?.altre_disponibili && (
              <button data-testid="calendar-studio-toggle" onClick={() => setCalStudio(!calStudio)}
                title={(cal?.altre_nomi || []).join(" · ")} className={`btn btn-piccolo ${calStudio ? "btn-g" : "btn-o"}`}>
                {(cal?.altre_nomi || []).length === 1 ? cal.altre_nomi[0] : "Altre agende"}
              </button>
            )}
            <button data-testid="new-reminder-btn" onClick={() => setReminderOpen(true)} className="btn btn-o btn-piccolo">
              Promemoria
            </button>
            {[[1, "Oggi"], [7, "Settimana"], [31, "Mese"]].map(([g, l]) => (
              <button key={g} data-testid={`cal-mode-${g}`} onClick={() => setCalDays(g)}
                className={`btn btn-piccolo ${calDays === g ? "btn-g" : "btn-o"}`}>{l}</button>
            ))}
          </>}>
          <div className="sub" data-testid="agenda-native">
            {cal && !cal.connected ? (
              <div className="riga text-[#5A5A5A]">
                Collega Google in <Link to="/impostazioni">Impostazioni</Link> per vedere l'agenda.
              </div>
            ) : calLoading ? (
              <div className="riga text-[#5A5A5A]"><Loader2 className="w-4 h-4 animate-spin" /> Carico l'agenda…</div>
            ) : events.length === 0 ? (
              <div className="riga text-[#5A5A5A]">Nessun evento in questo periodo.</div>
            ) : Object.entries(eventsByDay).map(([day, evs]) => (
              <div key={day}>
                <div className="subt"><span className="capitalize">{day}</span></div>
                {evs.map((e) => (
                  <div key={`${e.calendario_user_id || ""}-${e.id}`} data-testid="agenda-event" className="riga group">
                    <span className="w-[68px] shrink-0 text-[#4A4A48]">
                      {e.all_day ? "tutto il g." : new Date(e.start).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    {e.privato ? (
                      <span className="flex-grow min-w-0 text-[#5A5A5A] italic" data-testid={`agenda-event-privato-${e.id}`}>
                        Occupato · {e.calendario_label}
                      </span>
                    ) : (
                      <a href={e.htmlLink} target="_blank" rel="noreferrer" className="flex-grow min-w-0 no-underline text-[#1A1A1A]">
                        <span className="block truncate">{e.summary}</span>
                        <span className="block text-[12px] text-[#5A5A5A] truncate">
                          {e.calendario === "altra" ? `agenda di ${e.calendario_label}` : ""}
                          {e.location ? `${e.calendario === "altra" ? " · " : ""}${e.location}` : ""}
                        </span>
                      </a>
                    )}
                    {!e.privato && (
                      <button data-testid={`agenda-event-delete-${e.id}`} title="Elimina dal calendario"
                        onClick={() => eliminaEvento(e)}
                        className="shrink-0 text-[#8A2E2E]/60 hover:text-[#8A2E2E] opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))}
            <div className="nota-tenue">
              Eventi letti direttamente dal tuo Google Calendar collegato{cal?.error ? ` — ${cal.error}` : ""}.
            </div>
          </div>
        </Sezione>

        {/* 4 · I compiti di oggi */}
        <Sezione testid="sez-mio-giorno" titolo="I miei compiti di oggi" piatto>
          <MioGiorno senzaCornice />
        </Sezione>

        {/* 5 · Documenti dai clienti */}
        <Sezione testid="sez-nuovi-documenti" titolo="Documenti ricevuti dai clienti">
          <NuoviDocumenti senzaCornice />
        </Sezione>

        {/* 6 · Documenti in scadenza */}
        <Sezione testid="sez-scadenze-documenti" titolo="Documenti in scadenza">
          <ScadenzeDocumenti senzaCornice />
        </Sezione>

        {/* 7 · Priorità e scadenze degli step */}
        <Sezione testid="sez-priorita" titolo="In evidenza">
          <div className="grid md:grid-cols-2 gap-4 min-w-0">
            <div className="sub min-w-0" data-testid="priorita-box">
              <div className="subt"><span>Priorità</span>
                <span className="text-[12px] font-normal normal-case tracking-normal text-[#5A5A5A]">{(d.priorita || []).length}</span>
              </div>
              {(d.priorita || []).length === 0 ? (
                <div className="riga text-[#5A5A5A]">Nessuna pratica prioritaria. Attiva la stella in una pratica per tenerla qui.</div>
              ) : d.priorita.map((p) => (
                <Link key={p.id} to={`/pratiche/${p.id}`} data-testid={`priorita-item-${p.id}`}
                  className="riga no-underline text-[#1A1A1A] hover:bg-[#FAFAF7] transition-colors">
                  <span className="flex-grow min-w-0">
                    <span className="block truncate font-semibold text-[#1B2A4B]">{p.cliente_nome || "—"}</span>
                    <span className="block text-[12px] text-[#5A5A5A] truncate">{p.tipo_pratica_nome} · {p.stato_corrente}</span>
                  </span>
                  {p.residuo > 0 && <span className="shrink-0 text-[13px] font-bold text-[#8A6E2F]">{eur(p.residuo)}</span>}
                </Link>
              ))}
            </div>
            <div className="sub min-w-0" data-testid="scadenze-box">
              <div className="subt"><span>Scadenze dei prossimi 15 giorni</span>
                <span className="text-[12px] font-normal normal-case tracking-normal text-[#5A5A5A]">{(d.scadenze || []).length}</span>
              </div>
              {(d.scadenze || []).length === 0 ? (
                <div className="riga text-[#5A5A5A]">Nessuna scadenza imminente.</div>
              ) : d.scadenze.map((s, i) => (
                <div key={`${s.pratica}-${s.step}-${i}`} className="riga">
                  <span className="flex-grow min-w-0 truncate">{s.pratica} — {s.step}</span>
                  <span className="shrink-0 text-[13px] text-[#4A4A48]">{dataIt(s.scadenza)}</span>
                </div>
              ))}
            </div>
          </div>
        </Sezione>

        {/* 8 · Ricerca pratica */}
        <Sezione testid="sez-ricerca" titolo="Ricerca pratica" aperta={false}>
          <div className="flex flex-col sm:flex-row gap-2.5" data-testid="pratiche-search">
            <div className="relative flex-grow">
              <Search className="w-4 h-4 text-[#5A5A5A] absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="pratiche-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca per nome del cliente…"
                className="w-full pl-9 pr-3 py-2.5 text-[14px] border border-[#C6C3BA] rounded-[4px] bg-white focus:outline-none focus:border-[#A8863F]" />
            </div>
            <select data-testid="pratiche-search-tipo" value={tipoF} onChange={(e) => setTipoF(e.target.value)}
              className="px-3 py-2.5 text-[14px] border border-[#C6C3BA] rounded-[4px] bg-white sm:w-64 focus:outline-none focus:border-[#A8863F]">
              <option value="">Tutte le tipologie</option>
              {tipiList.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {searchActive && (
            <div className="sub">
              <div className="subt"><span>Risultati</span>
                <span className="text-[12px] font-normal normal-case tracking-normal text-[#5A5A5A]">{foundPratiche.length}</span>
              </div>
              {foundPratiche.length === 0 ? (
                <div className="riga text-[#5A5A5A]" data-testid="ricerca-vuota">
                  Nessuna pratica trovata. Prova con il cognome del cliente o cambia tipologia.
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  {foundPratiche.map((p) => (
                    <Link key={p.id} to={`/pratiche/${p.id}`} data-testid={`search-result-${p.id}`}
                      className="riga no-underline text-[#1A1A1A] hover:bg-[#FAFAF7] transition-colors">
                      <span className="flex-grow min-w-0">
                        <span className="block truncate font-semibold text-[#1B2A4B]">
                          {p.cliente ? `${p.cliente.nome} ${p.cliente.cognome}` : "—"}
                        </span>
                        <span className="block text-[12px] text-[#5A5A5A] truncate">
                          {p.tipo_pratica_nome} · aperta il {dataIt(p.created_at)}
                        </span>
                      </span>
                      {vedeSoldi && (
                        <span className={`shrink-0 text-[13px] font-bold ${p.importo_totale - p.importo_pagato > 0 ? "text-[#8A2E2E]" : "text-[#2E5E3A]"}`}>
                          {eur(p.importo_totale - p.importo_pagato)}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </Sezione>

        {/* 9 · Provenienza dei clienti */}
        <Sezione testid="sez-mappa" titolo="I clienti di RB Studio" aperta={false} piatto>
          <ContactsMap senzaCornice />
        </Sezione>

        {/* 10 · News */}
        <Sezione testid="sez-news" titolo="News" aperta={false}
          azioni={<span className="text-[13px] text-[#E7D9B2]">immigrazione · lavoro · norme</span>}>
          <div className="sub" data-testid="news-card">
            {news === undefined ? (
              <div className="riga text-[#5A5A5A]"><Loader2 className="w-4 h-4 animate-spin" /> Carico le notizie…</div>
            ) : (news.items || []).length === 0 ? (
              <div className="riga text-[#5A5A5A]">Nessuna notizia disponibile al momento.</div>
            ) : news.items.map((n, i) => (
              <a key={n.link || i} href={n.link} target="_blank" rel="noreferrer" data-testid={`news-item-${i}`}
                className="riga no-underline text-[#1A1A1A] hover:bg-[#FAFAF7] transition-colors">
                <span className="w-[108px] shrink-0 text-[12px] font-bold uppercase tracking-[0.6px] text-[#8A6E2F]">{n.topic}</span>
                <span className="flex-grow min-w-0">
                  <span className="block">{n.title}</span>
                  <span className="block text-[12px] text-[#5A5A5A]">
                    {n.source}{n.published ? ` · ${dataIt(n.published)}` : ""}
                  </span>
                </span>
              </a>
            ))}
            <div className="nota-tenue">
              Ricerca automatica quotidiana da fonti di stampa. Verifica sempre la fonte ufficiale prima di agire.
            </div>
          </div>
        </Sezione>
      </div>

      {appOpen && <AppuntamentoDialog onClose={() => setAppOpen(false)} />}
      {sceltaOpen && (
        <SceltaClienteDialog titolo="Genera documento per…"
          onPick={(c) => { setSceltaOpen(false); setDocCliente(c); }}
          onClose={() => setSceltaOpen(false)} />
      )}
      <DocumentoDialog open={!!docCliente} clienteId={docCliente?.id}
        clienteNome={docCliente ? [docCliente.nome, docCliente.cognome].filter(Boolean).join(" ") : ""}
        onClose={() => setDocCliente(null)} />
      {reminderOpen && <QuickReminderDialog pratiche={allPratiche} onClose={() => setReminderOpen(false)} />}
    </div>
  );
}
